<template>
  <AppWithLoading :fetch="fetchCompanyWithUsers">
    <VForm v-scroll-to-first-error @submit.prevent="submit">
      <VRow no-gutters>
        <VCol
          cols="12"
          md="6"
          lg="5"
          xl="4"
        >
          <div class="font-weight-medium mb-4">
            {{ $t('vacancyInfo') }}
          </div>
          <VCombobox
            id="title"
            v-model="title"
            :autofocus="!title"
            :items="professions"
            :loading="isLoadingProfessions"
            :search-input.sync="title"
            append-icon=""
            hide-no-data
            no-filter
            outlined
            dense
            item-text="title"
            item-value="title"
            :return-object="false"
            :error-messages="errors.title"
            autocomplete="organization-title"
          >
            <template v-slot:label>
              {{ $t('vacancyName') }} *
            </template>
          </VCombobox>
        </VCol>
      </VRow>
      <VRow no-gutters class="mb-8">
        <VCol cols="12" lg="10" xl="6">
          <Editor
            v-model="description"
            :label="$t('description')"
            required
            :hint="$t('descriptionTip')"
            :error-messages="errors.description"
          />
        </VCol>
      </VRow>

      <VRow no-gutters wrap class="mb-4">
        <VCol cols="12" md="3" xl="2">
          <div class="font-weight-medium py-2">
            {{ $t('keySkills') }}
          </div>
        </VCol>
        <VCol
          cols="12"
          md="9"
          lg="7"
          xl="4"
          class="pt-2"
        >
          <VCombobox
            v-model="skills"
            :items="availableSkills"
            :loading="skillsLoading"
            :search-input.sync="skillsSearch"
            :delimiters="[';']"
            append-icon=""
            outlined
            dense
            multiple
            hide-no-data
            small-chips
            :label="$t('keySkillsLabel')"
            hide-selected
            background-color="transparent"
            autocomplete="off"
            :error-messages="errors.skills"
            class="vacancy-combobox"
          >
            <template v-slot:selection="{ attrs, item, parent, selected }">
              <VChip
                v-bind="attrs"
                :color="`${item.color} lighten-3`"
                :input-value="selected"
                label
                small
              >
                <span class="pr-1">
                  {{ item }}
                </span>
                <VIcon small @click="parent.selectItem(item)">
                  mdi-close
                </VIcon>
              </VChip>
            </template>
          </VCombobox>
        </VCol>
      </VRow>

      <VRow wrap no-gutters>
        <VCol cols="12" md="3" xl="2">
          <div class="font-weight-medium py-2">
            {{ $t('incomeLevel') }}
          </div>
        </VCol>

        <VCol
          cols="12"
          md="9"
          lg="7"
          xl="4"
          class="pt-2"
        >
          <VRow
            align="start"
            justify="space-between"
            wrap
            no-gutters
          >
            <VCol cols="12" md="6" class="d-flex">
              <VTextField
                v-model="salary.from"
                class="mr-1"
                outlined
                dense
                autocomplete="off"
                type="number"
                :label="$t('from')"
                :error-messages="errors['salary.from']"
              />
              <VTextField
                v-model="salary.to"
                class="mr-1"
                outlined
                dense
                autocomplete="off"
                type="number"
                :error-messages="errors['salary.to']"
                :label="$t('to')"
              />
            </VCol>
            <VCol cols="6" md="3" class="d-flex">
              <VSelect
                v-model="salary.currency"
                :items="currencies"
                class="mr-1"
                outlined
                dense
                :menu-props="{ offsetY: true, nudgeBottom: 10, attach: true }"
                :error-messages="errors['salary.currency']"
              >
                <template v-slot:item="{ item }">
                  {{ item.text }} ({{ item.symbol }})
                </template>
              </VSelect>
            </VCol>
            <VCol cols="6" md="3">
              <VSelect
                v-model="salary.payFrequency"
                :items="payFrequencies"
                outlined
                dense
                :menu-props="{ offsetY: true, nudgeBottom: 10, attach: true }"
                :error-messages="errors['salary.payFrequency']"
              />
            </VCol>
          </VRow>
        </VCol>
      </VRow>

      <VRow no-gutters class="mb-3">
        <VCol class="d-none d-md-block" md="3" xl="2" />

        <VCol cols="12" md="6" class="pt-2">
          <VRadioGroup v-model="salary.payType" :error-messages="errors['salary.payType']">
            <VRadio
              v-for="payType of payTypes"
              :key="payType.value"
              :label="payType.text"
              :value="payType.value"
              :error-messages="errors['salary.payType']"
              color="success"
              class="mb-3"
            />
          </VRadioGroup>
        </VCol>
      </VRow>

      <VRow wrap no-gutters class="mb-3">
        <VCol cols="12" md="3" xl="2">
          <div class="font-weight-medium py-2">
            {{ $t('experience') }}
          </div>
        </VCol>

        <VCol cols="12" md="6" class="pt-2">
          <VRadioGroup v-model="experience" :error-messages="errors.experience">
            <VRadio
              v-for="e in experiences"
              :key="e.value"
              :label="e.text"
              :value="e.value"
              :error-messages="errors.experience"
              color="success"
              class="mb-3"
            />
          </VRadioGroup>
        </VCol>
      </VRow>

      <VRow wrap no-gutters class="mb-3">
        <VCol cols="12" md="3" xl="2">
          <div class="font-weight-medium py-2">
            {{ $t('employment') }}
          </div>
        </VCol>

        <VCol cols="12" md="6" class="pt-2">
          <VRadioGroup :error-messages="errors.employment">
            <VCheckbox
              v-for="e of employments"
              :key="e.value"
              v-model="employment"
              :label="e.text"
              :value="e.value"
              :error-messages="errors.employment"
              color="success"
              class="mb-3"
            />
          </VRadioGroup>
        </VCol>
      </VRow>

      <VRow wrap no-gutters class="mb-3">
        <VCol cols="12" md="3" xl="2">
          <div class="font-weight-medium py-2">
            {{ $t('schedule') }}
          </div>
        </VCol>

        <VCol cols="12" md="6" class="pt-2">
          <VRadioGroup :error-messages="errors.schedule">
            <VCheckbox
              v-model="schedule"
              v-for="s in schedules"
              :key="s.value"
              :label="s.text"
              :value="s.value"
              color="success"
              class="mb-3"
            />
          </VRadioGroup>
        </VCol>
      </VRow>

      <VRow wrap no-gutters>
        <VCol cols="12" md="3" xl="2">
          <div class="font-weight-medium py-2 mb-1">
            {{ $t('address') }}
          </div>
        </VCol>

        <VCol
          cols="12"
          sm="9"
          md="7"
          lg="8"
          class="mr-sm-2 pt-2"
        >
          <VRow wrap align="start" no-gutters>
            <VCol
              cols="12"
              sm="9"
              md="7"
              lg="8"
              class="mr-sm-2"
            >
              <VSelect
                v-model="addressId"
                :items="addresses"
                :label="$t('chooseAddress')"
                outlined
                dense
                item-text="address"
                item-value="id"
                :no-data-text="$t('noAddress')"
                :error-messages="errors.addressId"
              />
            </VCol>
            <span
              class="primary--text pseudo-link mt-n2 mb-8 my-sm-2 body-2"
              @click="addressDialog = true"
            >
              {{ $t('addAddress') }}
            </span>
            <VDialog v-model="addressDialog" max-width="500">
              <VCard class="pa-5">
                <div class="title mb-4">
                  {{ $t('address') }}
                </div>
                <AddressGeocodeInput
                  v-model="newAddress"
                  :errors="newAddressErrors"
                  class="mb-2"
                  @input="newAddressErrors = []"
                  @converged="newAddressSaveButtonDisabled = false"
                  @diverged="newAddressSaveButtonDisabled = true"
                />
                <div class="text-right">
                  <VBtn
                    color="success"
                    outlined
                    class="mt-2"
                    @click="addressDialog = false"
                  >
                    {{ $t('close') }}
                  </VBtn>
                  <VBtn
                    color="success"
                    :disabled="newAddressSaveButtonDisabled"
                    :loading="newAddressAddLoading"
                    class="ml-2 mt-2"
                    elevation="0"
                    @click="saveAddress"
                  >
                    {{ $t('save') }}
                  </VBtn>
                </div>
              </VCard>
            </VDialog>
          </VRow>
        </VCol>
      </VRow>

      <VRow wrap no-gutters>
        <VCol cols="12" md="3" xl="2">
          <div class="font-weight-medium py-2 mb-1">
            {{ $t('userCreator') }}
          </div>
        </VCol>

        <VCol
          cols="12"
          sm="9"
          md="7"
          lg="8"
          class="mr-sm-2 pt-2"
        >
          <VRow wrap align="start" no-gutters>
            <VCol
              cols="12"
              sm="9"
              md="7"
              lg="8"
              class="mr-sm-2"
            >
              <VSelect
                outlined
                v-model="userCreatorId"
                :items="selectData"
                :label="$t('userCreatorLabel')"
                item-value="id"
                item-text="fullName"
                :error-messages="errors.userCreatorId"
              />
            </VCol>
          </VRow>
        </VCol>
      </VRow>

      <VRow wrap no-gutters>
        <VCol cols="12" md="3" xl="2">
          <div class="font-weight-medium py-2 pr-md-3">
            {{ $t('contactInfo') }}
          </div>
        </VCol>

        <VCol
          cols="12"
          md="6"
          lg="4"
          class="pt-2"
        >
          <VRadioGroup v-model="contacts" class="mb-3">
            <VRadio
              v-for="item of indicateContacts"
              :key="item.value"
              :label="item.text"
              :value="item.value"
              color="success"
              class="mb-3"
            />
          </VRadioGroup>

          <div v-show="showContactFields">
            <VTextField
              id="name"
              v-model="contactPerson.name"
              outlined
              dense
              type="text"
              autocomplete="name"
              :error-messages="errors['contactPerson.name']"
            >
              <template v-slot:label>
                {{ $t('contactPerson') }} *
              </template>
            </VTextField>

            <VTextField
              id="email"
              v-model="contactPerson.email"
              outlined
              dense
              type="text"
              autocomplete="email"
              :error-messages="errors['contactPerson.email']"
            >
              <template v-slot:label>
                {{ $t('email') }} *
              </template>
            </VTextField>

            <PhoneNumberInput
              :key="contactPerson.phone"
              v-model="contactPerson.phone"
              required
              :label="$t('phone')"
              :error-messages="errors['contactPerson.phone']"
            />

            <VTextarea
              v-model="contactPerson.comment"
              outlined
              :error-messages="errors['contactPerson.comment']"
            >
              <template v-slot:label>
                {{ $t('comment') }}
              </template>
            </VTextarea>
          </div>
        </VCol>
      </VRow>

      <VRow wrap no-gutters>
        <VCol cols="12" md="3" xl="2">
          <div class="font-weight-medium py-2 pr-md-3">
            {{ $t('vacancyVisibility') }}
          </div>
        </VCol>
        <VCol
          cols="12"
          md="6"
          lg="4"
          class="pt-2"
        >
          <VRadioGroup v-model="visibility">
            <VRadio
              v-for="item in visibilityEnum"
              :key="item.value"
              :label="item.text"
              :value="item.value"
              color="success"
              class="mb-3"
            />
          </VRadioGroup>
        </VCol>
      </VRow>

      <VRow no-gutters>
        <slot name="buttons" :submit="submit">
          <VBtn
            color="success"
            type="submit"
            dark
            elevation="0"
            :loading="loading"
          >
            {{ $t('save') }}
          </VBtn>
        </slot>
      </VRow>
    </VForm>
  </AppWithLoading>
</template>

<script>
import debounce from 'lodash.debounce';
import Profession from '@/orm/models/Profession/Profession';
import ValidationMixin from '@/mixins/ValidationMixin';
import Editor from '@/components/Input/Editor.vue';
import AddressGeocodeInput from '@/components/Address/AddressGeocodeInput.vue';
import Address from '@/orm/models/Address/Address';
import Company from '@/orm/models/Company/Company';
import { safeGet, normalizePhoneNumber } from '@/functions';
import { response } from '@/orm/functions';
import ScrollToFirstError from '@/directives/ScrollToFirstError';
import PhoneNumberInput from '@/components/Input/PhoneNumberInput.vue';
import Vacancy from '@/orm/models/Vacancy/Vacancy';
import Skill from '@/orm/models/Skill/Skill';
import { payFrequencies, payTypes } from '@/enums';
import { normalizationData } from '@/helpers/forms';
import useCompany from '@/compositions/useCompany';
import AppWithLoading from '@/components/AppWithLoading';
import useUser from '@/compositions/useUser';

export default {
  name: 'VacancyForm',
  components: {
    AppWithLoading,
    PhoneNumberInput,
    AddressGeocodeInput,
    Editor,
  },
  directives: { ScrollToFirstError },
  mixins: [ValidationMixin],
  props: {
    user: {
      type: Object,
      required: true,
    },
    vacancy: {
      type: Object,
      default() {
        return {
          id: null,
          title: null,
          description: '',
          skills: [],
          salary: null,
          experience: 'beginner',
          employment: ['fulltime'],
          schedule: ['regular'],
          addressId: null,
          contactPerson: null,
          visibility: 'all',
          userCreatorId: null,
        };
      },
    },
  },
  setup() {
    const { fetch, company, addresses } = useCompany();
    const { getUsers, users } = useUser();

    async function fetchCompanyWithUsers() {
      await fetch();
      await getUsers();
    }

    return {
      fetch,
      company,
      addresses,
      getUsers,
      users,
      fetchCompanyWithUsers,
    };
  },
  data() {
    return {
      id: this.vacancy.id,
      title: this.vacancy.title,
      description: this.vacancy.description,
      skills: this.vacancy.skills,
      salary: {
        from: this.vacancy.salary?.from,
        to: this.vacancy.salary?.to,
        currency: this.vacancy.salary?.currency || (this.company.country.alpha2Code === 'RU' ? 'RUB' : 'USD'),
        payFrequency: this.vacancy.salary?.payFrequency || payFrequencies.MONTH,
        payType: this.vacancy.salary?.payType ?? payTypes.GROSS,
      },
      experience: this.vacancy.experience,
      employment: this.vacancy.employment,
      schedule: this.vacancy.schedule,
      addressId: safeGet(this.vacancy, ['addressId']),
      contacts: this.getContactsType(),
      contactPerson: this.getDefaultContactPerson(),
      visibility: this.vacancy.visibility,
      userCreatorId: safeGet(this.vacancy, ['creator', 'id']) ?? this.user.id,

      currencies: this.company.country.currencies.map((c) => ({
        value: c.id,
        text: this.$t(`currencies.${c.id}`),
        symbol: c.symbol,
      })),
      payFrequencies: this.company.country.payFrequencies.map((p) => ({
        value: p.id,
        text: this.$t(`payFrequencies.${p.id}`),
      })),
      payTypes: [
        { value: payTypes.NET, text: this.$t(`payTypes.${payTypes.NET}`) },
        { value: payTypes.GROSS, text: this.$t(`payTypes.${payTypes.GROSS}`) },
      ],
      experiences: [
        { value: 'beginner', text: this.$t('beginner') },
        { value: '1-3 years', text: this.$t('years1') },
        { value: '3-6 years', text: this.$t('years2') },
        { value: '6+ years', text: this.$t('years3') },
      ],
      employments: [
        { value: 'fulltime', text: this.$t('fulltime') },
        { value: 'parttime', text: this.$t('parttime') },
        { value: 'project', text: this.$t('project') },
        { value: 'voluntary', text: this.$t('voluntary') },
        { value: 'internship', text: this.$t('internship') },
      ],
      schedules: [
        { value: 'regular', text: this.$t('fullDay') },
        { value: 'shift', text: this.$t('shift') },
        { value: 'flex', text: this.$t('flexible') },
        { value: 'remote', text: this.$t('remote') },
        { value: 'watch', text: this.$t('watch') },
      ],
      indicateContacts: [
        { value: 'specify', text: this.$t('specify') },
        { value: 'not-specify', text: this.$t('doNotSpecify') },
      ],
      visibilityEnum: [
        { value: 'all', text: this.$t('availableToAll') },
        { value: 'invitation', text: this.$t('availableByInvitation') },
      ],

      skillsSearch: '',
      skillsLoading: false,
      addressDialog: false,
      newAddress: { id: null, address: null },
      newAddressErrors: [],
      newAddressAddLoading: false,
      newAddressSaveButtonDisabled: true,
      localizedSkills: [],
      professions: [],
      isLoadingProfessions: false,
      callProfessionsApi: debounce(async (title) => {
        if (!title || title.length <= 2) {
          this.professions = [];
          return;
        }

        this.isLoadingProfessions = true;
        try {
          this.professions = await response(Profession.api().find(title));
        } finally {
          this.isLoadingProfessions = false;
        }
      }, 1000),
    };
  },
  computed: {
    locale() {
      return this.$i18n.locale;
    },
    showContactFields() {
      return this.contacts === 'specify';
    },
    availableSkills() {
      return this.localizedSkills || [];
    },
    salaryFromValidationError() {
      return safeGet(this.errors, ['salaryFrom']) || safeGet(this.errors, ['salaryFrom.amount']);
    },
    salaryToValidationError() {
      return safeGet(this.errors, ['salaryTo']) || safeGet(this.errors, ['salaryTo.amount']);
    },
    selectData() {
      if (!this.users) {
        return [];
      }

      return this.users.map((user) => {
        user.fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();

        if (!user.position) {
          return user;
        }

        user.fullName = `${user.fullName}, ${user.position}`;

        return user;
      });
    },
  },
  watch: {
    locale() {
      this.fetchSkills();
    },
    skills() {
      if (!this.skills.every((str) => /\S/.test(str))) {
        this.skills = this.skills.filter((str) => /\S/.test(str));
      }
    },
    title(title) {
      this.callProfessionsApi(title);
    },
    userCreatorId(newId) {
      const selectedUser = this.selectData.find((user) => {
        return user.id === newId;
      });

      this.contactPerson.name = `${selectedUser.firstName} ${selectedUser.lastName}`;
      this.contactPerson.email = selectedUser.email;
      this.contactPerson.phone = selectedUser.phone;
    },
  },
  created() {
    this.fetchSkills();
  },
  methods: {
    async submit() {
      return this.id !== null ? this.update() : this.store();
    },
    async store() {
      return this.withValidationHandling(async () => response(Vacancy.api().store(this.company.id, this.getData())));
    },
    async update() {
      return this.withValidationHandling(async () => response(Vacancy.api().update(this.id, this.getData())));
    },
    async saveAddress() {
      return this.withApiErrorHandling(
        async () => {
          this.newAddressErrors = [];
          const address = (await Address.api().store(this.newAddress)).entities.addresses[0];
          await Company.api().update(this.company.id, {
            addresses: [...this.company.addresses.map((a) => a.id), address.id],
          });
          this.addressId = address.id;
          this.addressDialog = false;
        },
        (e) => {
          this.newAddressErrors = [e.code === 422 ? this.$t('addressNotCorrect') : e.message];
        },
        'newAddressAddLoading',
      );
    },
    getData() {
      return normalizationData({
        title: this.title,
        description: this.description,
        skills: this.skills,
        salary: this.salary?.from || this.salary?.to ? this.salary : null,
        experience: this.experience,
        employment: this.employment,
        schedule: this.schedule,
        addressId: this.addressId,
        visibility: this.visibility,
        contactPerson: this.showContactFields
          ? {
            name: this.contactPerson.name,
            email: this.contactPerson.email,
            phone: normalizePhoneNumber(this.contactPerson.phone),
            comment: this.contactPerson.comment,
          }
          : null,
        userCreatorId: this.userCreatorId,
      });
    },
    getContactsType() {
      if (this.vacancy.id === null) {
        return 'specify';
      }

      if (this.vacancy.contactPerson === null) {
        return 'not-specify';
      }

      return 'specify';
    },
    getDefaultContactPerson() {
      if (this.vacancy.contactPerson !== null) {
        return { ...this.vacancy.contactPerson };
      }
      return {
        name: `${this.user.firstName} ${this.user.lastName}`,
        email: this.user.email,
        phone: this.user.phone,
        comment: '',
      };
    },
    fetchSkills() {
      this.withLoading(async () => {
        this.localizedSkills = await response(Skill.api().fetch(this.locale));
      }, 'skillsLoading');
    },
  },
};
</script>

<style lang="scss">
.vacancy-combobox {
  &.v-select.v-input--dense .v-chip {
    margin: 2px 4px 2px 0;
    padding: 0 0.375rem 0 0.625rem;
    &.v-size--small {
      height: 20px;
    }
  }
}

.v-messages {
  min-height: 0;
}

.v-input__slot {
  margin-bottom: 0;
}
.v-input--selection-controls {
  padding-top: 0;
}

.v-list-item__title {
  font-size: 13px;
}
</style>

<i18n locale="en" lang="yaml">
  vacancyName: Job name
  vacancyInfo: Job information
  description: Description
  descriptionTip: >-
    At least 150 characters. Please do not include contact information in job description.
  keySkills: Key skills
  keySkillsLabel: Use a semicolon (;) to separate
  incomeLevel: Income level
  from: From
  to: To
  net: net
  gross: gross
  experience: Experience
  beginner: no experience
  years1: 1-3 years
  years2: 3-6 years
  years3: 6+ years
  employment: Employment
  fulltime: full time
  parttime: part time
  project: project
  voluntary: volunteering
  internship: internship
  schedule: Schedule
  fullDay: full day
  shift: shift
  flexible: flexible
  remote: remote
  watch: remote shift
  address: Address
  chooseAddress: Choose address
  noAddress: No addresses
  addAddress: Add address
  close: Close
  save: Save
  contactInfo: Contact information
  specify: specify
  doNotSpecify: do not specify
  contactPerson: Contact person
  email: E-mail
  phone: Phone
  comment: Comment
  vacancyVisibility: Job visibility
  availableToAll: Available to all
  availableByInvitation: Available by invitation only
  addressNotCorrect: Incorrect address
  onlySpacesErr: Skill must contain characters
  currencies.RUB: Rubles
  currencies.USD: US dollar
  currencies.EUR: Euro
  currencies.GBP: Great Britain Pound
  currencies.BYN: Rubles
  currencies.ILS: Sheqel
  currencies.KGS: Som
  currencies.KZT: Tenge
  currencies.UAH: Hryvnia
  currencies.AED: Dirham
  currencies.AUD: Australian Dollar
  currencies.CAD: Canadian dollar
  currencies.CHF: Swiss franc
  currencies.NZD: New Zealand dollar
  currencies.HKD: Hong Kong dollar
  currencies.JPY: Japanese yen
  currencies.CNY: Chinese yuan
  payFrequencies.year: Per year
  payFrequencies.month: Per month
  payFrequencies.week: Per week
  payFrequencies.day: Per day
  payFrequencies.hour: Per hour
  payTypes.net: net
  payTypes.gross: gross
  userCreator: Responsible for the job
  userCreatorLabel: Choose responsible for the job
</i18n>

<i18n locale="ru" lang="yaml">
  vacancyName: Название вакансии
  vacancyInfo: Информация о вакансии
  description: Описание
  descriptionTip: >-
    Не менее 150 символов. Пожалуйста, не вставляйте контактную информацию в описание вакансий.
  keySkills: Ключевые навыки
  keySkillsLabel: Разделитель - точка-запятая (;)
  incomeLevel: Уровень дохода
  from: От
  to: До
  experience: Опыт работы
  beginner: начало карьеры
  years1: 1-3 года
  years2: 3-6 лет
  years3: больше 6 лет
  employment: Занятость
  fulltime: полная занятость
  parttime: частичная занятость
  project: проектная/временная работа
  voluntary: волонтерство
  internship: стажировка
  schedule: График работы
  fullDay: полный день
  shift: сменный график
  flexible: гибкий график
  remote: удаленная работа
  watch: вахтовый метод
  address: Место работы
  chooseAddress: Выберите адрес
  noAddress: Нет адресов
  addAddress: Добавить адрес
  close: Закрыть
  save: Сохранить
  contactInfo: Контактная информация работодателя
  specify: указать
  doNotSpecify: не указывать
  contactPerson: Контактное лицо
  email: E-Mail
  phone: Телефон
  comment: Комментарий (удобное время для звонка по номеру)
  vacancyVisibility: Видимость вакансии
  availableToAll: Доступна всем
  availableByInvitation: Доступна только по приглашению
  addressNotCorrect: Некорректный адрес
  onlySpacesErr: Навык должен содержать непробельные символы
  currencies.RUB: Рубли
  currencies.USD: Американский доллар
  currencies.EUR: Евро
  currencies.GBP: Фунт стерлинг
  currencies.BYN: Бел. Рублей
  currencies.ILS: Шекелей
  currencies.KGS: Сом
  currencies.KZT: Тенге
  currencies.UAH: Гривен
  currencies.AED: Дирхам ОАЭ
  currencies.AUD: Австралийский доллар
  currencies.CAD: Канадский доллар
  currencies.CHF: Швейцарский франк
  currencies.NZD: Новозеландский доллар
  currencies.HKD: Гонконгский доллар
  currencies.JPY: Японская иена
  currencies.CNY: Китайский юань
  payFrequencies.year: В год
  payFrequencies.month: В месяц
  payFrequencies.week: В неделю
  payFrequencies.day: В день
  payFrequencies.hour: В час
  payTypes.net: до вычета НДФЛ
  payTypes.gross: на руки
  userCreator: Ответственный за вакансию
  userCreatorLabel: Выберите ответственного за вакансию
</i18n>
